import { FunctionComponent } from 'react';

import styled from 'styled-components';

import { PanelProps } from './PanelProps';

export const Panel: FunctionComponent<PanelProps> = ({
  children,
  className,
  variant = 'default',
  padding = 'default',
  border = true,
  shadow = false,
  ...rest
}) => (
  <StyledPanel className={className} $variant={variant} $padding={padding} $border={border} $shadow={shadow} {...rest}>
    {children}
  </StyledPanel>
);

Panel.displayName = 'Panel';

const StyledPanel = styled.div<{
  $variant: 'default' | 'header' | 'sidebar' | 'content' | 'footer';
  $padding: 'none' | 'compact' | 'default' | 'large';
  $border: boolean;
  $shadow: boolean;
}>`
  /* Base layout - fully compatible with react-resizable-panels */
  display: flex;
  flex-direction: column;
  position: relative;

  /* Let react-resizable-panels handle all sizing */
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  /* Default styling */
  background: ${({ theme }) => theme.colors.palette.white};
  border-radius: ${({ theme }) => theme.radii.base};

  /* Border styling */
  ${({ theme, $border }) =>
    $border &&
    `
    border: ${theme.borderWidths.base} solid ${theme.colors.borderBase ?? theme.colors.palette.grayscale[2]};
  `}

  /* Shadow styling */
  ${({ theme, $shadow }) =>
    $shadow &&
    `
    box-shadow: ${theme.shadows.primary};
  `}

  /* Padding that doesn't interfere with percentage calculations */
  ${({ theme, $padding }) => {
    switch ($padding) {
      case 'none':
        return 'padding: 0;';
      case 'compact':
        return `padding: ${theme.space.spacing20};`;
      case 'large':
        return `padding: ${theme.space.spacing50};`;
      default:
        return `padding: ${theme.space.spacing30};`;
    }
  }}

  /* Variant styling - no conflicting sizing properties */
  ${({ theme, $variant }) => {
    switch ($variant) {
      case 'header':
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.palette.white};
          border: none;
          border-radius: 0;
          font-weight: ${theme.fontWeights.semiBold};
          flex-shrink: 0; /* Prevent header from shrinking */
        `;
      case 'footer':
        return `
          background: ${theme.colors.palette.grayscale[0]};
          border-top: ${theme.borderWidths.base} solid ${theme.colors.borderBase ?? theme.colors.palette.grayscale[2]};
          border-left: none;
          border-right: none;
          border-bottom: none;
          border-radius: 0;
          font-size: ${theme.fontSizes.footnote};
          color: ${theme.colors.textSecondary ?? theme.colors.palette.grayscale[6]};
          flex-shrink: 0; /* Prevent footer from shrinking */
        `;
      case 'sidebar':
        return `
          background: ${theme.colors.palette.grayscale[0]};
          border: ${theme.borderWidths.base} solid ${theme.colors.borderBase ?? theme.colors.palette.grayscale[2]};
          border-radius: 0;
          overflow-y: auto; /* Allow sidebar content to scroll */
          /* Removed min-height - let react-resizable-panels control sizing */
        `;
      case 'content':
        return `
          background: ${theme.colors.palette.white};
          border: none;
          border-radius: 0;
          flex: 1 1 0; /* Allow content to grow and shrink with available space */
          overflow: auto; /* Content can scroll if needed */
        `;
      default:
        return `
          background: ${theme.colors.palette.white};
          border: ${theme.borderWidths.base} solid ${theme.colors.borderBase ?? theme.colors.palette.grayscale[2]};
        `;
    }
  }}
`;
